import { useLayoutEffect } from "react";
import { useData } from "data/data-provider";
import { useLocation, useNavigationType } from "react-router-dom";
import DesignARouter from "routers/DesignARouter";
import DesignBRouter from "routers/DesignBRouter";
import PostHogPageview from "analytics/PostHogPageview";
import PaymentLinkRedirect from "designs/Common/PaymentLinkRedirect/PaymentLinkRedirect";

function App() {
  const { loadingCompanyDetails, ourCompany } = useData();
  const location = useLocation();
  const navigationType = useNavigationType();

  // Layout effect so the history is updated before child useEffects (e.g.
  // BackRouteButton) read it on the same location change.
  useLayoutEffect(() => {
    const historyKey = "app_route_history";
    const currentPath = location.pathname + location.search;
    let history: string[] = [];

    try {
      const rawHistory = sessionStorage.getItem(historyKey);
      history = rawHistory ? (JSON.parse(rawHistory) as string[]) : [];
    } catch {
      history = [];
    }

    if (navigationType === "POP") {
      // Back/forward (or first load). Mirror the browser back stack by
      // dropping entries above the current path instead of re-appending —
      // otherwise BackRouteButton reads the page we just left as "previous".
      while (history.length && history[history.length - 1] !== currentPath) {
        history.pop();
      }
      if (history.length === 0) {
        history.push(currentPath);
      }
    } else if (navigationType === "REPLACE") {
      if (history.length) {
        history[history.length - 1] = currentPath;
      } else {
        history.push(currentPath);
      }
    } else if (history[history.length - 1] !== currentPath) {
      history.push(currentPath);
    }

    sessionStorage.setItem(historyKey, JSON.stringify(history.slice(-30)));
  }, [location.pathname, location.search, navigationType]);

  // Design B re-enabled on QA (2026-06-09) so both designs are testable here.
  // Renter site renders Design B when the tenant's company API `default_template`
  // is "design2", else Design A. (Stage/master keep the kill switch.)
  const isDesignB = ourCompany?.default_template === "design2";

  return (
    // Section backgrounds (banner image, header band, footer, etc.) need to
    // run edge-to-edge on any viewport width — so we DON'T cap the outer
    // wrapper here. The 1440px content cap is applied to Tailwind's
    // `container` class via the tailwind config (`screens: { '2xl': '1440px' }`)
    // so it only constrains content inside sections, not the section
    // backgrounds themselves.
    // 1NOW-1640: min-h-screen so the tenant background covers the full
    // viewport. Without it any page shorter than the window (e.g. /vehicles at
    // 729px in a 900px viewport) ended in a band of browser-default white
    // below the footer.
    <div className="bg-bg-color min-h-screen">
      {location.pathname.startsWith("/pay/") ? (
        // Payment short-link redirect lands renters here from SMS. We
        // bypass the whole rental-site shell (no SiteHeader, no Footer,
        // no waiting for company-details to load) so the only thing the
        // renter ever sees is the redirect splash for ~200-400ms before
        // window.location.replace bounces them to Stripe/Square. [1NOW-1530]
        <PaymentLinkRedirect />
      ) : loadingCompanyDetails ? (
        <div className="flex justify-center items-center col-span-full min-h-[100vh]">
          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
        </div>
      ) : (
        <div className="text-base text-text-color bg-bg-color">
          <PostHogPageview />
          {isDesignB ? <DesignBRouter /> : <DesignARouter />}
        </div>
      )}
    </div>
  );
}

export default App;
